import {useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import api from "../api/axios";
import {Trade, TradeGroup} from "../types/trades";
import {upsertTradeGroup} from "../store/slices/tradeGroupsSlice";

type BatchData = {
    partnerSteamId: string;
    tradeGroupId: string;
    tradeToken: string;
    tradeSecurityCode: string;
    message: string;
    itemAssetId: string | string[];
}

type PartnerDetailsDTO = {
    partnerProfileName: string;
    partnerAvatarUrl: string;
    partnerLevel: number;
    partnerYearsOfService: number;
    partnerSteamSignUpDate: string;
}

interface ConfirmModalData {
    open: boolean;
    groupKey: string;
    targets: Trade[];
    tradeSecurityCode: string;
    partnerProfileName?: string;
    partnerAvatarUrl?: string;
    partnerLevel?: number;
    partnerYearsOfService?: number;
    partnerSteamSignUpDate?: string;
}

const STEAM_ERROR_CODES: {[key: string]: string} = {
    '2': 'FAIL',
    '11': 'INVALID_STATE',
    '15': 'ACCESS_DENIED',
    '16': 'TIMEOUT',
    '20': 'SERVICE_UNAVAILABLE',
    '25': 'LIMIT_EXCEEDED',
    '26': 'REVOKED',
    '27': 'ALREADY_REDEEMED',
    '34': 'LOGON_SESSION_REPLACED'
};

export const useTradeBatchActions = (
    handleLocalUpdate: (
        tradeId: string,
        newStatus: string,
        localDate: Date,
        groupUpdates?: {tradeGroupId: string; tradeGroupLocked: boolean}
    ) => void,
    onRefresh: () => Promise<void>
): {
    batchLoading: {[key: string]: boolean};
    setBatchLoading: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
    confirmModalData: ConfirmModalData | null;
    setConfirmModalData: React.Dispatch<React.SetStateAction<ConfirmModalData | null>>;
    handleBatchAccept: (groupKey: string, group: Trade[]) => Promise<void>;
    handleBatchSend: (groupKey: string, group: Trade[]) => Promise<void>;
    handleOpenMobileConfirmation: (groupKey: string, group: Trade[]) => Promise<void>;
    handleConfirmMobileDispatched: () => Promise<void>;
} => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [batchLoading, setBatchLoading] = useState<{[key: string]: boolean}>({});
    const [confirmModalData, setConfirmModalData] = useState<ConfirmModalData | null>(null);

    const handleBatchAccept = async (groupKey: string, group: Trade[]): Promise<void> => {
        const targets = group.filter(t => t.effectiveTradeStatus === 'WAITING_FOR_SELLER_CONFIRM');

        if (targets.length === 0) return;

        setBatchLoading(prev => ({...prev, [groupKey]: true}));

        try {
            const tradeIds = targets.map(t => t.id);
            const response = await api.post<TradeGroup>('/user/trade/accept', {tradeIds});
            const tradeGroup = response.data;
            const now = new Date();

            dispatch(upsertTradeGroup(tradeGroup));

            targets.forEach(trade => {
                handleLocalUpdate(trade.id, 'WAITING_FOR_SELLER_SEND', now, {
                    tradeGroupId: tradeGroup.id,
                    tradeGroupLocked: true
                });
            });

            toast.success(t('Group trades accepted'));
        } catch (e) {
            console.error('Batch accept failed', e);
            toast.error(t('Failed to accept trades'));
        } finally {
            setBatchLoading(prev => ({...prev, [groupKey]: false}));
        }
    };

    const handleBatchSend = async (groupKey: string, group: Trade[]): Promise<void> => {
        const targets = group.filter(t => t.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND');

        if (targets.length === 0) return;

        setBatchLoading(prev => ({...prev, [groupKey]: true}));

        try {
            const tradeIds = targets.map(t => t.id);
            const tradeGroupId = group[0].tradeGroupId;

            if (!tradeGroupId) {
                throw new Error('Trade group ID is missing');
            }

            const response = await api.get(`/user/trade-group/${tradeGroupId}/send-details`);
            const batchData: BatchData = response.data;

            let normalizedAssetIds: string[] = [];

            if (Array.isArray(batchData.itemAssetId)) {
                normalizedAssetIds = batchData.itemAssetId.flatMap((id: string) =>
                    typeof id === 'string' ? id.split(',') : String(id)
                );
            } else if (typeof batchData.itemAssetId === 'string') {
                normalizedAssetIds = batchData.itemAssetId.split(',');
            } else if (batchData.itemAssetId) {
                normalizedAssetIds = [String(batchData.itemAssetId)];
            }

            const requestId = Math.floor(Math.random() * 1000000);

            const timeoutId = setTimeout(() => {
                window.removeEventListener('message', handleExtensionResponse);
                console.error('Extension response timed out after 10 seconds');
                toast.error(t('history.errors.send_failed') + ': Timeout');
                setBatchLoading(prev => ({...prev, [groupKey]: false}));
            }, 10000);

            const handleExtensionResponse = async (event: MessageEvent): Promise<void> => {
                if (event.data?.target !== 'MOONSKINS_FRONTEND') return;
                if (String(event.data?.id) !== String(requestId)) return;

                console.log('Frontend received postMessage:', event.data);

                clearTimeout(timeoutId);
                window.removeEventListener('message', handleExtensionResponse);

                const resData = event.data?.response;
                const errorMsg = event.data?.error;

                const hasMobileAuth =
                    resData?.needs_mobile_confirmation === true ||
                    resData?.json?.needs_mobile_confirmation === true;

                const isSuccessStatus = resData?.status === 200;

                if (!errorMsg && (hasMobileAuth || isSuccessStatus)) {
                    console.log('Extension successfully created trade offer:', resData);

                    const tradeOfferId = resData?.json?.tradeofferid || resData?.tradeofferid;

                    if (tradeOfferId) {
                        try {
                            await api.post(`/trade-group/${tradeGroupId}/annotate-steam-trade/${tradeOfferId}`);
                            toast.info(t('Created Steam Offer'));
                        } catch (annotateError) {
                            console.error('Failed to annotate steam trade offer', annotateError);
                        }
                    }

                    try {
                        const partnerRes = await api.get<PartnerDetailsDTO>('/user/trade/partner-details', {
                            params: {tradeIds}
                        });

                        const partnerData = partnerRes.data;

                        setConfirmModalData({
                            open: true,
                            groupKey,
                            targets,
                            tradeSecurityCode: batchData.tradeSecurityCode,
                            partnerProfileName: partnerData.partnerProfileName || '',
                            partnerAvatarUrl: partnerData.partnerAvatarUrl || '',
                            partnerLevel: partnerData.partnerLevel || 0,
                            partnerYearsOfService: partnerData.partnerYearsOfService || 0,
                            partnerSteamSignUpDate: partnerData.partnerSteamSignUpDate || ''
                        });
                    } catch (partnerError) {
                        toast.error(t('Failed to fetch partner details'));
                        setBatchLoading(prev => ({...prev, [groupKey]: false}));
                    }
                } else {
                    const rawError = errorMsg ? String(errorMsg) : 'Unknown Error';
                    const mappedError = STEAM_ERROR_CODES[rawError] || rawError;

                    console.error('Extension failed to create trade offer:', mappedError);

                    const translationKey = `history.errors.${mappedError}`;
                    const hasTranslation = t(translationKey) !== translationKey;

                    if (hasTranslation) {
                        toast.error(t(translationKey));
                    } else {
                        toast.error(`${t('history.errors.send_failed')}: ${mappedError}`);
                    }

                    setBatchLoading(prev => ({...prev, [groupKey]: false}));
                }
            };

            window.addEventListener('message', handleExtensionResponse);

            window.postMessage({
                target: 'MOONSKINS_EXTENSION',
                version: 1,
                request_type: 11,
                payload: {
                    tradeGroupId,
                    toSteamID64: batchData.partnerSteamId,
                    tradeToken: batchData.tradeToken,
                    message: batchData.message,
                    assetIDsToGive: normalizedAssetIds,
                    assetIDsToReceive: [],
                    forceEnglish: true
                },
                id: requestId
            }, '*');
        } catch (e) {
            console.error('Batch send initialization failed', e);
            toast.error(t('Failed to fetch send details'));
            setBatchLoading(prev => ({...prev, [groupKey]: false}));
        }
    };

    const handleOpenMobileConfirmation = async (
        groupKey: string,
        group: Trade[]
    ): Promise<void> => {
        if (group.length === 0) return;

        const tradeGroupId = group[0].tradeGroupId;

        if (!tradeGroupId) {
            console.error('Cannot open mobile confirmation: trade group ID is missing');
            toast.error(t('Failed to fetch trade details'));
            return;
        }

        setBatchLoading(prev => ({...prev, [groupKey]: true}));

        try {
            const tradeIds = group.map(trade => trade.id);

            const [sendDetailsResponse, partnerResponse] = await Promise.all([
                api.get<BatchData>(`/user/trade-group/${tradeGroupId}/send-details`),
                api.get<PartnerDetailsDTO>('/user/trade/partner-details', {
                    params: {tradeIds}
                })
            ]);

            const sendDetailsData = sendDetailsResponse.data;
            const partnerData = partnerResponse.data;

            setConfirmModalData({
                open: true,
                groupKey,
                targets: group,
                tradeSecurityCode: sendDetailsData.tradeSecurityCode || '',
                partnerProfileName: partnerData.partnerProfileName || '',
                partnerAvatarUrl: partnerData.partnerAvatarUrl || '',
                partnerLevel: partnerData.partnerLevel || 0,
                partnerYearsOfService: partnerData.partnerYearsOfService || 0,
                partnerSteamSignUpDate: partnerData.partnerSteamSignUpDate || ''
            });
        } catch (e) {
            console.error('Failed to reopen mobile confirmation modal', e);
            toast.error(t('Failed to fetch trade details'));
        } finally {
            setBatchLoading(prev => ({...prev, [groupKey]: false}));
        }
    };

    const handleConfirmMobileDispatched = async (): Promise<void> => {
        if (!confirmModalData) return;

        const {groupKey} = confirmModalData;

        setConfirmModalData(null);

        await new Promise<void>((resolve) => {
            let settled = false;

            const cleanup = () => {
                window.removeEventListener('message', handleExtensionSync);
                clearTimeout(timeoutId);
            };

            const finish = async () => {
                if (settled) {
                    return;
                }

                settled = true;
                cleanup();

                await onRefresh();

                setBatchLoading(prev => ({...prev, [groupKey]: false}));
                resolve();
            };

            const handleExtensionSync = (event: MessageEvent) => {
                if (event.data?.target !== 'MOONSKINS_WEBAPP') {
                    return;
                }

                if (
                    event.data?.action !== 'TRADE_STATUS_UPDATED' &&
                    event.data?.action !== 'TRADE_STATUS_CHECKED'
                ) {
                    return;
                }

                void finish();
            };

            const timeoutId = setTimeout(() => {
                void finish();
            }, 10000);

            window.addEventListener('message', handleExtensionSync);
        });
    };

    return {
        batchLoading,
        setBatchLoading,
        confirmModalData,
        setConfirmModalData,
        handleBatchAccept,
        handleBatchSend,
        handleOpenMobileConfirmation,
        handleConfirmMobileDispatched
    };
};